import { ApexOptions } from "apexcharts";
import Chart from "react-apexcharts";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";

interface BarChartInterface {
  titulo?: string;
  cor?: string[];
  series: any;
  categories: any;
  horizontal?: boolean;
  max?: number;
  min?: number;
  yTitleFormatter?: any;
  showX?: boolean;
  distributedBar?: boolean;
  showGridX?: boolean;
  showGridY?: boolean;
  legend?: ApexLegend;
  strokeDashArray?: number;
  height?: number;
  containewWidth?: number;
  defaultBarWidth?: string | number | undefined;
  borderBarRadius?: number;
  labelColor?: string;
  yAxisFormatter?: (val: number) => string;
  showTooltip?: boolean;
  annotations?: ApexAnnotations;
  stacked?: boolean;
  forecastDataPointsCount?: number;
  datalabels?: any;
}

const barConfigByArtistNumber = (
  length: number,
  defaultBarWidth: string | number | undefined,
  borderBarRadius: number = 0
) => {
  if (defaultBarWidth === null) return;
  if (defaultBarWidth)
    return { columnWidth: defaultBarWidth, borderRadius: borderBarRadius };
  const barConfigs: any = {
    1: { columnWidth: 25, borderRadius: 5 },
    2: { columnWidth: 30, borderRadius: 10 },
    3: { columnWidth: 35, borderRadius: 10 },
    4: { columnWidth: 40, borderRadius: 10 },
    5: { columnWidth: 40, borderRadius: 10 },
  };
  return barConfigs[length];
};

const BarChart: React.FC<BarChartInterface> = (props) => {
  const {
    titulo = "",
    cor,
    series,
    categories,
    horizontal = true,
    max,
    min,
    showGridX,
    showGridY,
    yTitleFormatter,
    showX = true,
    distributedBar,
    legend,
    height = "auto",
    containewWidth = "100%",
    strokeDashArray = 0,
    defaultBarWidth = 0,
    labelColor = "#fff",
    showTooltip = true,
    yAxisFormatter,
    annotations,
    stacked = false,
    forecastDataPointsCount,
    borderBarRadius,
    datalabels = { enabled: false },
  } = props;
  const breakpoint = useBreakpoint();

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: height,
      stacked: stacked,
      toolbar: {
        show: false,
      },
    },

    plotOptions: {
      bar: {
        distributed: distributedBar,
        horizontal: horizontal,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
        borderRadius: borderBarRadius,
        ...barConfigByArtistNumber(
          series.length,
          defaultBarWidth,
          borderBarRadius
        ),
      },
    },
    title: {
      text: titulo,
      style: { color: labelColor },
      align: "left",
    },

    colors: cor,
    dataLabels: { ...datalabels },
    stroke: {
      // lineCap: "round",
      curve: "smooth",
      width: breakpoint.xxl ? 12 : breakpoint.lg ? 10 : breakpoint.md ? 6 : 3,
      colors: ["transparent"],
    },
    legend: legend,
    forecastDataPoints: {
      count: forecastDataPointsCount,
      fillOpacity: 0.5,
      strokeWidth: undefined,
      dashArray: 4,
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: "12px",
        },
        formatter: yAxisFormatter,
      },
      max: max,
      min: min,
      tickAmount: 5,
    },
    grid: {
      borderColor: "#8a8a8a80",
      strokeDashArray: strokeDashArray,
      xaxis: {
        lines: {
          show: showGridX,
        },
      },
      yaxis: {
        lines: {
          show: showGridY,
        },
      },
    },
    xaxis: {
      categories: categories,
      max: max,
      min: min,
      axisBorder: { show: false },
      labels: {
        style: {
          colors: "white",
          fontSize: "12px",
        },
      },
    },
    tooltip: {
      theme: "light",
      enabled: showTooltip,
      x: {
        show: showX,
      },
      y: {
        title: {
          formatter: yTitleFormatter,
        },
      },
    },
    annotations: annotations || {},
  };

  return (
    <Chart
      options={options}
      series={series}
      type="bar"
      height={height}
      width={containewWidth}
    />
  );
};

export default BarChart;
